import { useEffect, useState } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { useCachedState } from "@/lib/swr";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { ScrollText, PhoneCall, Search, CheckCircle2, XCircle, PencilLine, Save, RotateCcw } from "lucide-react";

function RecognitionCheck() {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const check = async () => {
    if (!phone.trim()) { toast.error("Enter a phone number"); return; }
    setBusy(true);
    try {
      const r = await api.get(`/admin/voice/recognize-check`, { params: { phone } });
      setResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Check failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-sand-200 p-6" data-testid="recognition-check-card">
      <div className="flex items-center gap-2 font-heading font-bold mb-1">
        <PhoneCall className="h-4 w-4 text-redline-500" strokeWidth={1.5} /> Caller Recognition Check
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        See exactly what voice recognition sees for any phone number — which communities know it,
        the resident it maps to, and the greeting the AI would use. Use this to diagnose
        &ldquo;the concierge doesn&apos;t know me&rdquo; reports.
      </p>
      <div className="flex gap-2">
        <input data-testid="recognition-check-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && check()}
          placeholder="+1 512 555 0012"
          className="flex-1 rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
        <button data-testid="recognition-check-run" disabled={busy} onClick={check}
          className="inline-flex items-center gap-2 rounded-full bg-redline-500 text-white font-semibold px-5 py-2.5 hover:bg-redline-600 transition-colors disabled:opacity-50">
          <Search className="h-4 w-4" /> {busy ? "Checking…" : "Check"}
        </button>
      </div>
      {result && (
        <div className="mt-4 space-y-3" data-testid="recognition-check-result">
          <div className={`flex items-center gap-2 text-sm font-semibold ${result.recognized_anywhere ? "text-emerald-700" : "text-redline-600"}`}>
            {result.recognized_anywhere
              ? <><CheckCircle2 className="h-4 w-4" /> Recognized — {result.matches.length} match{result.matches.length > 1 ? "es" : ""}</>
              : <><XCircle className="h-4 w-4" /> Not recognized on any roster</>}
            <span className="text-xs font-normal text-muted-foreground">normalized: {result.normalized || "—"}</span>
          </div>
          {result.why && <p className="text-sm text-muted-foreground bg-sand-50 border border-sand-200 rounded-xl p-3">{result.why}</p>}
          {(result.matches || []).map((m) => (
            <div key={m.tenant_id} className="border border-sand-200 rounded-xl p-4 text-sm space-y-1">
              <div className="font-semibold">{m.community}</div>
              <div>Resident: <span className="font-medium">{m.person || "(household match only)"}</span>{homeLabel(m) && <> · {homeLabel(m)}</>}</div>
              <div className="text-xs text-muted-foreground">stored as: {m.person_phone_as_stored || "—"} · voice AI {m.voice_ai_enabled ? "on" : "off"} · unknown-caller mode: {m.residents_only_mode}</div>
              <div className="text-xs bg-sand-50 rounded-lg px-3 py-2 mt-1">Greeting: &ldquo;{m.greeting_preview}&rdquo;</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PlatformRules() {
  const [doc, setDoc] = useCachedState("platform-rules", null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/platform-rules").then((r) => setDoc(r.data)).catch(() => setDoc({ content: "_Could not load rules._" }));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/platform-rules", { content: draft });
      toast.success("Platform rules updated");
      setEditing(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true);
    try {
      await api.delete("/admin/platform-rules");
      toast.success("Back to the shipped version");
      setEditing(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className={`space-y-6 ${editing ? "" : "max-w-4xl"}`} data-testid="platform-rules-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <PageTitle title={<><ScrollText className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Platform Rules</>} about={<>Every business rule and product constraint this platform enforces — the living registry of decisions you&apos;ve made.
            {doc?.updated_at && <span className="text-xs"> · Last updated {new Date(doc.updated_at).toLocaleDateString()}</span>}
            {doc?.customized && <span className="ml-2 rounded-full bg-sky-50 text-sky-700 border border-sky-200 text-[11px] font-bold px-2 py-0.5 align-middle">Edited{doc.updated_by ? ` by ${doc.updated_by}` : ""}</span>}</>} />
        </div>
        {doc && !editing && (
          <div className="flex items-center gap-2">
            {doc.customized && (
              <button data-testid="rules-reset-btn" disabled={busy} onClick={reset}
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-sand-300 text-ink/70 text-sm font-semibold px-4 py-2 hover:border-ink hover:text-ink transition-colors">
                <RotateCcw className="h-3.5 w-3.5" /> Reset to shipped
              </button>
            )}
            <button data-testid="rules-edit-btn" onClick={() => { setDraft(doc.content); setEditing(true); }}
              className="inline-flex items-center gap-2 rounded-full bg-ink text-white text-sm font-semibold px-5 py-2 hover:bg-ink/85 transition-colors">
              <PencilLine className="h-4 w-4" /> Edit rules
            </button>
          </div>
        )}
        {editing && (
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(false)} className="rounded-full bg-white border border-sand-300 text-sm font-semibold px-4 py-2 hover:border-ink transition-colors">Cancel</button>
            <button data-testid="rules-save-btn" disabled={busy} onClick={save}
              className="inline-flex items-center gap-2 rounded-full bg-redline-500 text-white text-sm font-semibold px-5 py-2 hover:bg-redline-600 transition-colors disabled:opacity-50">
              <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save rules"}
            </button>
          </div>
        )}
      </div>

      {!editing && <RecognitionCheck />}

      {editing ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="rules-editor">
          <div className="bg-[#1a1614] rounded-2xl border border-sand-200 overflow-hidden flex flex-col">
            <div className="px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-sand-400 border-b border-white/10">Markdown</div>
            <textarea data-testid="rules-editor-textarea" value={draft} onChange={(e) => setDraft(e.target.value)}
              spellCheck={false}
              className="flex-1 w-full min-h-[70vh] bg-transparent text-sand-100 font-mono text-[13px] leading-relaxed p-4 outline-none resize-none" />
          </div>
          <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden">
            <div className="px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground border-b border-sand-100">Live preview</div>
            <div className="p-6 max-h-[74vh] overflow-y-auto prose prose-sm max-w-none prose-headings:font-heading prose-h2:text-lg prose-h2:mt-8 prose-h2:mb-3 prose-h1:text-2xl prose-li:my-0.5 prose-hr:my-6">
              <ReactMarkdown>{draft}</ReactMarkdown>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-sand-200 p-8">
          {!doc ? (
            <div className="flex items-center justify-center h-40"><div className="h-8 w-8 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
          ) : (
            <div className="prose prose-sm max-w-none prose-headings:font-heading prose-h2:text-lg prose-h2:mt-8 prose-h2:mb-3 prose-h1:text-2xl prose-li:my-0.5 prose-hr:my-6" data-testid="platform-rules-content">
              <ReactMarkdown>{doc.content}</ReactMarkdown>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
